import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import Env from '../../../../util/env';
import {getJWT} from '../../../../common/util/util-auth';
import Resources from '../../../../data/services/resources';
import {processResponse} from '../../../../data/services/api-util';
import OcrSplitDialog from '../../../../common/components/modal/ocr-split-dialog';
import ModalConfirm from '../../../../common/components/modal/modal-confirm';
import BillExpenseOcrForm, {getDefaultAccount, getHeaderFields, itemsFromExtraction, validateOcrForm} from './bill-expense-ocr-form';
import {matchVendor} from './vendor-match';
import {matchProductServiceCandidate, matchTaxRateCandidate} from './item-match-helpers';

async function searchQuick(resource, query) {
    const response = await axios.get(
        Env.getApiUrl('api/' + resource, {query, offset: 0, limit: 1}),
        {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
    );
    const result = processResponse(response);
    return result && result.status === 0 ? (result.data.list[0] ?? null) : null;
}

async function fetchTaxCandidates() {
    const response = await axios.get(
        Env.getApiUrl('api/' + Resources.AccountingTaxes, {offset: 0, limit: 50}),
        {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
    );
    const result = processResponse(response);
    return result && result.status === 0 ? result.data.list : [];
}

async function enrichItems(items) {
    const taxCandidates = await fetchTaxCandidates();

    return Promise.all((items ?? []).map(async (item) => {
        const productCandidates = item.ProductServiceID
            ? await axios.get(
                Env.getApiUrl('api/' + Resources.ProductsServices, {query: item.ProductServiceID, offset: 0, limit: 5}),
                {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
            ).then((response) => {
                const result = processResponse(response);
                return result && result.status === 0 ? result.data.list : [];
            })
            : [];

        const matchedProductService = matchProductServiceCandidate(item.ProductServiceID ?? '', productCandidates);
        const matchedTax = matchTaxRateCandidate(Number(item.TaxAmount) || 0, Number(item.Amount) || 0, taxCandidates);

        return {...item, matchedProductService, matchedTax};
    }));
}

export default function BillExpenseOcrDialog({show, file, onClose, onSave, translate, defaults}) {
    const [isLoading, setIsLoading] = useState(() => false);
    const [isError, setIsError] = useState(() => false);
    const [noVendorFoundDialog, setNoVendorFoundDialog] = useState(() => false);

    // The form's state lives here, not inside BillExpenseOcrForm: OcrSplitDialog only renders the
    // form panel while it is visible, so toggling that panel off and on unmounts/remounts the form.
    // Owning mode/fields/items (and the validation error) here is what keeps the user's data entry
    // across that cycle, and what lets Save validate without the form being mounted at all.
    const [mode, setMode] = useState(() => 'bill');
    const [fields, setFields] = useState(() => null);
    const [items, setItems] = useState(() => null);
    const [errorMessage, setErrorMessage] = useState(() => null);

    const abortControllerRef = useRef(null);

    /** Data Events
     ================================================================= */
    function extractDocument() {
        if (!file) {
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setIsError(false);
        setFields(null);
        setItems(null);
        setErrorMessage(null);

        const formData = new FormData();
        formData.append('Document', file);

        axios.post(
            Env.getDocumentAIApiUrl('api/' + Resources.AiExtractBill, {}),
            formData,
            {headers: {Authorization: `Bearer ${getJWT().access_token}`}, signal: controller.signal}
        )
            .then(async (response) => {
                const data = response?.data?.data;
                if (!data) {
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }

                const [vendorResult, enrichedItems] = await Promise.all([
                    data.VendorName?.trim()
                        ? matchVendor(data.VendorName, {
                            searchOrganizations: (name) => searchQuick(Resources.OrganizationsQuick, name),
                            searchContacts: (name) => searchQuick(Resources.ContactsQuick, name),
                        })
                        : Promise.resolve({type: null, match: null}),
                    enrichItems(data.Items),
                ]);

                const initialOrganization = vendorResult.type === 'organization' ? vendorResult.match : null;

                // Populated before isLoading is cleared, so the form panel never renders without them.
                setFields(getHeaderFields(data, vendorResult, translate));
                setItems(itemsFromExtraction(enrichedItems, translate, getDefaultAccount(initialOrganization, defaults)));

                setNoVendorFoundDialog(!vendorResult.type);
                setIsLoading(false);
            })
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    setIsError(true);
                    setIsLoading(false);
                }
            });
    }

    /** UI Events
     ================================================================= */
    function handleSave() {
        // Validation reads the state owned here, so Save behaves identically whether or not the
        // shell's form panel is currently mounted.
        const {
            fields: validatedFields,
            items: validatedItems,
            errorMessage: nextErrorMessage,
            result,
        } = validateOcrForm(mode, fields, items, translate);

        setFields(validatedFields);
        setItems(validatedItems);
        setErrorMessage(nextErrorMessage);

        if (result) {
            onSave(result.fields, result.resource, [file]);
        }
    }

    /** Life cycle
     ================================================================= */
    useEffect(() => {
        if (show && file) {
            extractDocument();
        }
    }, [show, file]);

    /** Render
     ================================================================= */
    return (
        <>
            <OcrSplitDialog
                show={show}
                title={translate('btn.create_from_document')}
                file={file}
                isLoading={isLoading}
                isError={isError}
                onRetry={extractDocument}
                onClose={onClose}
                onSave={handleSave}
                saveLabel={translate('btn.save')}
                translate={translate}
                isBeta={true}
                renderForm={() => (
                    <BillExpenseOcrForm
                        mode={mode}
                        setMode={setMode}
                        fields={fields}
                        setFields={setFields}
                        items={items}
                        setItems={setItems}
                        errorMessage={errorMessage}
                        translate={translate}
                        defaults={defaults}
                    />
                )}
            />

            <ModalConfirm
                title={translate('text.Confirm')}
                show={noVendorFoundDialog}
                text={translate('text.OCR_no_vendor_found')}
                textClassName="text-sm text-tm-gray-900"
                buttonLabel={translate('btn.confirm')}
                translate={translate}
                onConfirm={() => setNoVendorFoundDialog(false)}
            />
        </>
    );
}
